import { skipToken, useQuery } from '@tanstack/react-query';

import { Clips } from '@air/api';
import { type Clip } from '@air/api/types';
import { FIVE_MINUTES } from '@air/utilities';

import { defaultClipGetOptions, getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';

export type PrivateWorkspaceAgentPillClipCardDetailsParams = Omit<
  NonNullable<Parameters<typeof Clips.getWithNoWorkspaceId>[0]>,
  'id' | 'options'
> & {
  /** A pill that carries no version has no clip to read. */
  id: Clip['id'] | undefined;
};

export type UsePrivateWorkspaceAgentPillClipCardDetailsOptions = {
  enabled: boolean;
};

/** Module scope so the memoised `select` is not recomputed on every render of an open card. */
const toPrivateWorkspaceAgentPillClipCardDetails = (
  clip: Awaited<ReturnType<typeof Clips.getWithNoWorkspaceId<typeof defaultClipGetOptions>>>,
) => ({
  boards: clip.boards,
  openCommentCount: clip.openCommentCount ?? null,
});

/**
 * Everything the asset preview card needs that the batched `Clips.listByIds` does not carry: the
 * open-comment count and the boards the location line names. Asking the batch for boards would put a
 * boards join, an ancestor CTE and a permission filter on every pill in the thread, so both ride this
 * per-open request instead. It has to be `getWithNoWorkspaceId`: the workspace-scoped `Clips.get`
 * service reads only `clipId` / `withBoards` / `safeGet` and drops `withOpenDiscussionStatus`. Passing
 * the app's shared `defaultClipGetOptions` keeps the chip on the app's own clip-get entry rather than
 * opening a second one. Both fields are a nicety, so a failure leaves the card without them rather
 * than in an error state.
 */
export const usePrivateWorkspaceAgentPillClipCardDetails = (
  { id }: PrivateWorkspaceAgentPillClipCardDetailsParams,
  options: UsePrivateWorkspaceAgentPillClipCardDetailsOptions,
) =>
  useQuery({
    // A version-less pill still gets a key inside the clip namespace, so `select` can never run against
    // whatever else the app parked on a shared empty key.
    queryKey: getClipWithoutWorkspaceIdKey({ clipId: id ?? '', options: defaultClipGetOptions }),
    queryFn: id ? () => Clips.getWithNoWorkspaceId({ id, options: defaultClipGetOptions }) : skipToken,
    enabled: options.enabled,
    select: toPrivateWorkspaceAgentPillClipCardDetails,
    // Matches the board pill: a count should not refetch every time the card reopens.
    staleTime: FIVE_MINUTES,
  });
